import { gql, useMutation } from '@apollo/client';
import toast from 'helpers/toast';
import { get } from 'lodash';
import * as immutable from 'object-path-immutable';
import { translate } from 'translations/i18n';

import { PROJECT_PAGE_FRAGMENT } from 'components/projects/pages/gql/projectPageFragment';
import { GET_PROJECT_PAGES } from 'components/projects/pages/gql/getProjectPages.graphql';
import steven from 'helpers/steven';

const CREATE_PROJECT_PAGE = gql`
  mutation CreateProjectPage($input: CreateProjectPageInput!) {
    createProjectPage(input: $input) {
      errors {
        error
        message
      }
      page {
        __typename
        ...ProjectPageFragment
      }
    }
  }
  ${PROJECT_PAGE_FRAGMENT}
`;

const useCreateProjectPage = ({ projectNodeId, onSuccess }) => {
  const errorsFromData = (data) => get(data, 'createProjectPage.errors', null);

  const [createProjectPageMutation, { loading }] = useMutation(
    CREATE_PROJECT_PAGE,
    {
      update(cache, { data }) {
        const errors = errorsFromData(data);
        if (errors.length) {
          return;
        }

        const newProjectPage = data.createProjectPage.page;
        const variables = { projectNodeId };

        const cachedProjectPages = cache.readQuery({
          query: GET_PROJECT_PAGES,
          variables,
        });

        if (cachedProjectPages) {
          const updatedProjectPages = immutable.insert(
            cachedProjectPages,
            `viewer.project.pages.nodes`,
            newProjectPage,
            0,
          );

          cache.writeQuery({
            query: GET_PROJECT_PAGES,
            variables,
            data: updatedProjectPages,
          });
        }
      },
      onCompleted: (res) => {
        const errors = get(res, 'createProjectPage.errors', null);
        if (errors.length) {
          toast.error(translate('projectPage.hooks.createError'));
          return;
        }

        const page = get(res, 'createProjectPage.page', null);
        onSuccess(page);

        steven.entityCrud({
          entity_id: `${page.id}`,
          entity_type: 'page',
          action: 'created',
        });
      },
      onError: () => {
        toast.error(translate('projectPage.hooks.createError'));
      },
    },
  );

  const createProjectPage = (extraVariables = {}) =>
    createProjectPageMutation({
      variables: {
        input: {
          projectId: projectNodeId,
          ...extraVariables,
        },
      },
    });

  return {
    createProjectPage,
    loading,
  };
};

export default useCreateProjectPage;
